import { BeneficiaryFilterDateType } from '@repo/shared/dtos/beneficiary'
import { Button } from '@repo/ui/button'
import { useT } from '@repo/ui/hooks'
import { Input } from '@repo/ui/input'
import { Label } from '@repo/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@repo/ui/select'
import { onInput } from '@repo/ui/utils'
import { addDays } from 'date-fns'
import { useState } from 'react'

import { DatePickerWithPresets } from '@/components/DatePickerWithPressets'

import { FilterFormProps } from './types'

export default function DateFilterForm(props: FilterFormProps<BeneficiaryFilterDateType>) {
  const { filter, onChange, onCancel } = props

  const [form, setForm] = useState<BeneficiaryFilterDateType>(filter)
  const { t, tc } = useT({ keyPrefix: 'beneficiary.filters' })

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    event.stopPropagation()

    // Swap values if needed for 'between' operator
    let submittedForm = form
    if (form.operator === 'between' && form.otherValue && form.value > form.otherValue) {
      submittedForm = { ...form, value: form.otherValue, otherValue: form.value }
    }

    onChange(submittedForm)
    onCancel()
  }

  const handleOperatorChange = (operator: BeneficiaryFilterDateType['operator']) => {
    const newForm = { key: form.key, type: form.type }
    const now = new Date().getTime()
    switch (operator) {
      case 'current_week':
      case 'current_month':
      case 'current_year':
        return setForm({ ...newForm, operator })

      case 'in_the_last':
      case 'in_the_next':
        return setForm({ ...newForm, operator, days: 2 })

      case 'eq':
      case 'neq':
      case 'gt':
      case 'lt':
        return setForm({ ...newForm, operator, value: now })

      case 'between':
        return setForm({
          ...newForm,
          operator,
          value: now,
          otherValue: addDays(now, 1).getTime(),
        })

      case 'is_null':
      case 'is_not_null':
        return setForm({ ...newForm, operator } as BeneficiaryFilterDateType)

      default:
        throw new Error('Unsupported operator')
    }
  }

  return (
    <form
      onSubmit={(event) => {
        handleSubmit(event)
      }}
      className="space-y-6"
    >
      <div className="space-y-4">
        <Select value={form.operator} onValueChange={handleOperatorChange}>
          <SelectTrigger>
            <SelectValue placeholder={t('selectOperator')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="is_null">{t('operators.is_null')}</SelectItem>
            <SelectItem value="is_not_null">{t('operators.is_not_null')}</SelectItem>
            <SelectItem value="in_the_last">{t('operators.in_the_last')}</SelectItem>
            <SelectItem value="in_the_next">{t('operators.in_the_next')}</SelectItem>
            <SelectItem value="current_week">{t('operators.current_week')}</SelectItem>
            <SelectItem value="current_month">{t('operators.current_month')}</SelectItem>
            <SelectItem value="current_year">{t('operators.current_year')}</SelectItem>
            <SelectItem value="eq">{t('operators.eq')}</SelectItem>
            <SelectItem value="neq">{t('operators.neq')}</SelectItem>
            <SelectItem value="gt">{t('operators.gt')}</SelectItem>
            <SelectItem value="lt">{t('operators.lt')}</SelectItem>
            <SelectItem value="between">{t('operators.between')}</SelectItem>
          </SelectContent>
        </Select>

        {form.operator === 'in_the_last' && (
          <div>
            <Label>{tc('numberOfDays', { cfl: true })}</Label>
            <Input
              type="number"
              value={form.days ?? 2}
              onChange={onInput((v) => {
                const value = Number(v)
                setForm({ ...form, days: value >= 2 ? value : 2 })
              })}
            />
          </div>
        )}

        {form.operator === 'in_the_next' && (
          <div>
            <Label>{tc('numberOfDays', { cfl: true })}</Label>
            <Input
              type="number"
              value={form.days ?? 1}
              onChange={onInput((v) => {
                const value = Number(v)
                setForm({ ...form, days: value >= 1 ? value : 1 })
              })}
            />
          </div>
        )}

        {['eq', 'neq', 'gt', 'lt'].includes(form.operator) && 'value' in form && (
          <DatePickerWithPresets
            canSetNoDate={false}
            dateHandler={{
              date: new Date(form.value),
              setDate: (date) =>
                setForm({ ...form, value: date?.getTime() ?? new Date().getTime() }),
            }}
            disablePastDates={false}
            closePopoverOnSelect={true}
          />
        )}

        {form.operator === 'between' && (
          <div className="flex w-[30rem] items-center gap-2">
            <DatePickerWithPresets
              canSetNoDate={false}
              dateHandler={{
                date: new Date(form.value),
                setDate: (date) =>
                  setForm({ ...form, value: date?.getTime() ?? new Date().getTime() }),
              }}
              disablePastDates={false}
              closePopoverOnSelect={true}
            />
            <span className="text-primary-500 text-sm">{t('operators.and')}</span>
            <DatePickerWithPresets
              canSetNoDate={false}
              dateHandler={{
                date: form.otherValue ? new Date(form.otherValue) : undefined,
                setDate: (date) =>
                  setForm({ ...form, otherValue: date?.getTime() ?? new Date().getTime() }),
              }}
              disablePastDates={false}
              closePopoverOnSelect={true}
            />
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" onClick={onCancel} variant="ghost">
          {t('form.cancelButton')}
        </Button>
        <Button type="submit">{t('form.applyButton')}</Button>
      </div>
    </form>
  )
}
